import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Vote, 
  Plus, 
  LogOut, 
  Users, 
  Gift,
  ExternalLink,
  Trash2,
  MoreHorizontal,
  ChevronRight,
  Gamepad2,
  Shuffle,
  CircleDot,
  Ticket,
  Mail,
  Phone,
  TrendingUp,
  Database,
  Menu,
  Settings,
  CreditCard,
  Building2,
  Download,
  Image
} from "lucide-react";
import AnimatedLogo from "../components/AnimatedLogo";
import AccessStatusBadge from "../components/AccessStatusBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { drawsAPI, pollsAPI, statsAPI, gamesAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import ThemeToggle from "../components/ThemeToggle";
import UserMenu from "../components/UserMenu";
import { DrawCard, PollCard } from "../components/DrawPollCards";
import { InstallAppButton } from "../components/AppHeader";
import NotificationBell from "../components/NotificationBell";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [draws, setDraws] = useState([]);
  const [polls, setPolls] = useState([]);
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [drawsRes, pollsRes, statsRes, gamesRes] = await Promise.all([
        drawsAPI.list(),
        pollsAPI.list(),
        statsAPI.get(),
        gamesAPI.list().catch(() => ({ data: [] }))
      ]);
      setDraws(drawsRes.data);
      setPolls(pollsRes.data);
      setStats(statsRes.data);
      setGames(gamesRes.data || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraw = async (id) => {
    if (!window.confirm("Supprimer ce tirage ?")) return;
    try {
      await drawsAPI.delete(id);
      setDraws(draws.filter(d => d.id !== id));
      toast.success("Tirage supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeletePoll = async (id) => {
    if (!window.confirm("Supprimer ce sondage ?")) return;
    try {
      await pollsAPI.delete(id);
      setPolls(polls.filter(p => p.id !== id));
      toast.success("Sondage supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDeleteGame = async (id) => {
    if (!window.confirm("Supprimer ce jeu ?")) return;
    try {
      await gamesAPI.delete(id);
      setGames(games.filter(g => g.id !== id));
      toast.success("Jeu supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatusBadge = (status, isPaid) => {
    if (status === "completed") {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Terminé</Badge>;
    }
    if (status === "active") {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Actif</Badge>;
    }
    if (status === "closed") {
      return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Fermé</Badge>;
    }
    if (!isPaid) {
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Non payé</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Brouillon</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between gap-2">
          {/* Logo - always animated */}
          <div className="flex-shrink-0">
            <AnimatedLogo linkTo="/" showAnimation={true} size="small" className="sm:hidden" />
            <AnimatedLogo linkTo="/" showAnimation={true} size="default" className="hidden sm:flex" />
          </div>
          
          {/* Right side - Mobile Menu + User menu */}
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {/* Access Status Badge */}
            <div className="hidden sm:block">
              <AccessStatusBadge />
            </div>
            
            {/* Install App Button */}
            <InstallAppButton variant="icon" />
            
            {/* Mobile Navigation Menu */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1 px-2">
                    <Menu className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 z-[100]">
                  <DropdownMenuItem asChild>
                    <Link to="/draws/new" className="gap-3">
                      <Shuffle className="w-4 h-4 text-primary" />
                      <span>Nouveau tirage</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/polls/new" className="gap-3">
                      <Vote className="w-4 h-4 text-purple-500" />
                      <span>Nouveau sondage</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/games/new" className="gap-3">
                      <Gamepad2 className="w-4 h-4 text-pink-500" />
                      <span>Nouveau jeu</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/billing" className="gap-3">
                      <CreditCard className="w-4 h-4 text-green-500" />
                      <span>Facturation</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/media" className="gap-3">
                      <Image className="w-4 h-4 text-purple-500" />
                      <span>Galerie Média</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings/company" className="gap-3">
                      <Building2 className="w-4 h-4 text-blue-500" />
                      <span>Mon entreprise</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="gap-3">
                      <Settings className="w-4 h-4 text-slate-500" />
                      <span>Paramètres</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <NotificationBell />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome Message */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>
            Bonjour, {user?.contact_name?.split(' ')[0] || user?.company_name || 'là'} 👋
          </h1>
          <p className="text-muted-foreground mt-1">
            Bienvenue sur votre espace TIREO. Que souhaitez-vous créer aujourd'hui ?
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shuffle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.total_draws || 0}</p>
                  <p className="text-xs text-muted-foreground">Tirages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Vote className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.total_polls || 0}</p>
                  <p className="text-xs text-muted-foreground">Sondages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.completed_draws || 0}</p>
                  <p className="text-xs text-muted-foreground">Terminés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.active_polls || 0}</p>
                  <p className="text-xs text-muted-foreground">Actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Database Stats - Participants Quality */}
        <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5 text-primary" />
              Ma base de données
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Total Participants */}
              <div className="bg-card rounded-xl p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Participants</span>
                </div>
                <p className="text-3xl font-bold">{stats?.total_members || 0}</p>
              </div>
              
              {/* Emails */}
              <div className="bg-card rounded-xl p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-muted-foreground">Emails</span>
                </div>
                <p className="text-3xl font-bold">{stats?.emails_count || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.total_members > 0 
                    ? `${Math.round((stats?.emails_count / stats?.total_members) * 100)}% de la base`
                    : "0%"
                  }
                </p>
              </div>
              
              {/* Phones */}
              <div className="bg-card rounded-xl p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-muted-foreground">Téléphones</span>
                </div>
                <p className="text-3xl font-bold">{stats?.phones_count || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.total_members > 0 
                    ? `${Math.round((stats?.phones_count / stats?.total_members) * 100)}% de la base`
                    : "0%"
                  }
                </p>
              </div>
              
              {/* Qualification */}
              <div className="bg-card rounded-xl p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-muted-foreground">Qualification</span>
                </div>
                <p className="text-3xl font-bold">{stats?.qualification_percent || 0}%</p>
                <div className="w-full bg-muted rounded-full h-2 mt-2">
                  <div 
                    className="bg-amber-500 h-2 rounded-full transition-all"
                    style={{ width: `${stats?.qualification_percent || 0}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Prizes pending */}
            {stats?.prizes_pending > 0 && (
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Gift className="w-6 h-6 text-amber-500" />
                  <div>
                    <p className="font-bold text-foreground">{stats.prizes_pending} lot(s) à remettre</p>
                    <p className="text-sm text-muted-foreground">En attente de récupération par les gagnants</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-500" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions Principales - Boutons directs vers assistants */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">Créer</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/draws/new">
              <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-draw-btn">
                <Shuffle className="w-4 h-4 mr-2" />
                Tirage au sort
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/polls/new">
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="new-poll-btn">
                <Vote className="w-4 h-4 mr-2" />
                Sondage
              </Button>
            </Link>
            <Link to="/games/new">
              <Button className="rounded-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white" data-testid="new-game-btn">
                <Gamepad2 className="w-4 h-4 mr-2" />
                Jeu interactif
              </Button>
            </Link>
          </div>
        </div>

        {/* Mes listes */}
        <div className="mb-8">
          <Link to="/lists">
            <Button variant="outline" className="rounded-full" data-testid="lists-btn">
              <Users className="w-4 h-4 mr-2" />
              Mes listes de participants ({stats?.total_members || 0})
            </Button>
          </Link>
        </div>

        {/* Draws Section */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
            Mes tirages au sort
          </h2>
          
          {draws.length === 0 ? (
            <Card className="bg-card border-dashed">
              <CardContent className="p-8 text-center">
                <Shuffle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Aucun tirage</h3>
                <p className="text-muted-foreground mb-4">Créez votre premier tirage au sort</p>
                <Link to="/draws/new">
                  <Button className="rounded-full bg-primary hover:bg-primary/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un tirage
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {draws.map((draw) => (
                <DrawCard
                  key={draw.id}
                  draw={draw}
                  onDelete={handleDeleteDraw}
                  onNavigate={navigate}
                />
              ))}
            </div>
          )}
        </section>

        {/* Polls Section */}
        <section>
          <h2 className="text-xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
            Mes sondages
          </h2>
          
          {polls.length === 0 ? (
            <Card className="bg-card border-dashed">
              <CardContent className="p-8 text-center">
                <Vote className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Aucun sondage</h3>
                <p className="text-muted-foreground mb-4">Créez votre premier sondage ou vote</p>
                <Link to="/polls/new">
                  <Button variant="outline" className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un sondage
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {polls.map((poll) => (
                <PollCard
                  key={poll.id}
                  poll={poll}
                  onDelete={handleDeletePoll}
                  onNavigate={navigate}
                />
              ))}
            </div>
          )}
        </section>

        {/* Games Section */}
        <section>
          <h2 className="text-xl font-semibold text-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
            Mes jeux interactifs
          </h2>
          
          {games.length === 0 ? (
            <Card className="bg-card border-dashed">
              <CardContent className="p-8 text-center">
                <Gamepad2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Aucun jeu</h3>
                <p className="text-muted-foreground mb-4">Créez une roue de la fortune ou une carte à gratter</p>
                <Link to="/games/new">
                  <Button className="rounded-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un jeu
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {games.map((game) => (
                <Card key={game.id} className="bg-card hover:border-primary/30 transition-colors group" data-testid={`game-card-${game.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          game.game_type === "wheel"
                            ? "bg-gradient-to-br from-amber-400 to-orange-500"
                            : "bg-gradient-to-br from-emerald-400 to-teal-500"
                        }`}>
                          {game.game_type === "wheel" ? (
                            <CircleDot className="w-5 h-5 text-white" />
                          ) : (
                            <Ticket className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-lg line-clamp-1">{game.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {game.game_type === "wheel" ? "Roue" : "Grattage"}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/games/${game.id}`)}>
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`/game/${game.id}`, '_blank')}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Tester le jeu
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => handleDeleteGame(game.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <Gift className="w-4 h-4" />
                        {game.prizes?.length || 0} lots
                      </span>
                      <span>{game.total_plays || 0} participations</span>
                    </div>
                    <div className="flex items-center justify-between">
                      {game.status === "active" ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Actif</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Brouillon</Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(game.created_at)}
                      </span>
                    </div>
                    <Link to={`/games/${game.id}`} className="block mt-4">
                      <Button variant="outline" className="w-full rounded-full" size="sm">
                        Gérer le jeu
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
